import { useMemo, useState } from 'react'
import Flex from '@/components/priority/Flex/Flex'
import Icon from '@/components/priority/Icon/Icon'
import Text from '@/components/priority/Text/Text'
import { Button, Input } from 'components'
import * as S from '../InsertMenu/SlideoutMenu/SlideoutMenu.styled'
import { CategoryHeader } from './ServiceTemplates.styled'
import ServiceTemplateItem from './ServiceTemplateItem'
import {
  GetGlobalServiceInformationTemplatesNormalized,
  SERVICE_TEMPLATE_CATEGORY_ORDER,
} from '@messenger/types'
import type { ServiceTemplateCategory } from '@messenger/types'
import DesignerGate from '@/components/auth/DesignerGate'
import { useTranslation } from 'react-i18next'
import {
  countEditorProbeRender,
  isEditorProbeArmed,
} from '@/utils/editor/editorProbe'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'

type ServiceTemplatesMenuProps = {
  templates: GetGlobalServiceInformationTemplatesNormalized[]
  onTemplateSelect: (
    template: GetGlobalServiceInformationTemplatesNormalized,
  ) => void
  onClose?: (show: boolean) => void
  title?: string
  hasCloseButton?: boolean
  allowActions?: boolean
  /** A Save is in flight: the templates are listed, but none selects. */
  disabled?: boolean
  onCreateTemplate?: () => void
  onEditTemplate?: (
    template: GetGlobalServiceInformationTemplatesNormalized,
  ) => void
}

const ServiceTemplatesMenu = ({
  templates,
  onTemplateSelect,
  onClose,
  title,
  hasCloseButton = true,
  allowActions = true,
  disabled = false,
  onCreateTemplate,
  onEditTemplate,
}: ServiceTemplatesMenuProps) => {
  useEditorProbeLifetime('ServiceTemplatesMenu')
  if (isEditorProbeArmed())
    countEditorProbeRender(
      `ServiceTemplatesMenu.render:${templates.map((template) => template.id.slice(0, 8)).join(',')}`,
    )
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')

  const normalizedQuery = searchQuery.trim().toLowerCase()

  const filteredTemplates = useMemo(() => {
    if (!normalizedQuery) return templates
    return templates.filter((tpl) =>
      tpl.name.toLowerCase().includes(normalizedQuery),
    )
  }, [templates, normalizedQuery])

  const groupedTemplates = useMemo(() => {
    const groups = new Map<
      ServiceTemplateCategory,
      GetGlobalServiceInformationTemplatesNormalized[]
    >()

    for (const tpl of filteredTemplates) {
      const cat = (tpl.category as ServiceTemplateCategory) ?? 'DEFAULT'
      if (!groups.has(cat)) groups.set(cat, [])
      groups.get(cat)!.push(tpl)
    }

    return SERVICE_TEMPLATE_CATEGORY_ORDER.filter((cat) => groups.has(cat)).map(
      (cat) => ({
        category: cat,
        label: t(`editor.serviceTemplates.categories.${cat}`),
        templates: groups.get(cat)!,
      }),
    )
  }, [filteredTemplates, t])

  const hasMultipleCategories =
    groupedTemplates.length > 1 ||
    (groupedTemplates.length === 1 &&
      groupedTemplates[0].category !== 'DEFAULT')

  return (
    <Flex column width="100%" height="100%">
      <Flex
        justify="space-between"
        align="center"
        padding="24px 32px"
        gap="8px"
      >
        {title && (
          <Text weight="600" size="16px">
            {title}
          </Text>
        )}
        {hasCloseButton && (
          <Icon
            icon="exitModal"
            fill="gray1"
            // @ts-expect-error - clickable is not a valid prop for Icon
            onClick={() => onClose?.(false)}
            clickable
            data-testid="service-templates-menu-close"
          />
        )}
      </Flex>

      {templates.length > 0 && (
        <Flex padding="0 32px 12px 32px">
          <Input
            // @ts-expect-error - Input component value prop not properly typed
            type="text"
            aria-label={t('editor.serviceTemplates.searchPlaceholder')}
            placeholder={t('editor.serviceTemplates.searchPlaceholder')}
            value={[searchQuery, setSearchQuery]}
          />
        </Flex>
      )}

      {/* @ts-expect-error - overflowY/overflowX are valid props in styled component */}
      <S.Content column flex="1" overflowY="auto" overflowX="hidden">
        <Flex column padding="0 32px 32px 32px">
          {onCreateTemplate && (
            <DesignerGate>
              <Flex margin="0 0 24px 0" width="100%">
                <div style={{ width: '100%' }}>
                  <Button primary onClick={onCreateTemplate}>
                    + {t('editor.serviceTemplates.createNewTemplateButton')}
                  </Button>
                </div>
              </Flex>
            </DesignerGate>
          )}

          {filteredTemplates.length === 0 && (
            <Flex full center padding="40px" column>
              <Text color="gray2" margin="0 0 8px 0">
                {t('editor.serviceTemplates.noTemplatesAvailable')}
              </Text>
              {!searchQuery && (
                <Text color="gray3" size="14px">
                  {t('editor.serviceTemplates.createTemplateToGetStarted')}
                </Text>
              )}
            </Flex>
          )}

          {/* Render a single FLAT keyed list (headers interleaved as siblings)
              so template cards keep the same parent and key when grouping
              appears/disappears. Wrapping cards in per-category containers
              caused React to unmount and remount every card — and re-init
              every TinyMCE preview — whenever a template landed in a new
              category (e.g. the first PALLBEARERS template), which surfaced
              as page-wide blinking. */}
          {groupedTemplates.flatMap(
            ({ category, label, templates: catTpls }) => {
              const items = catTpls.map((template) => (
                <ServiceTemplateItem
                  key={template.id}
                  template={template}
                  onTemplateSelect={onTemplateSelect}
                  allowActions={allowActions}
                  disabled={disabled}
                  onEditTemplate={onEditTemplate}
                />
              ))

              return hasMultipleCategories
                ? [
                    <CategoryHeader key={`category-${category}`}>
                      {label}
                    </CategoryHeader>,
                    ...items,
                  ]
                : items
            },
          )}
        </Flex>
      </S.Content>
    </Flex>
  )
}

export default ServiceTemplatesMenu
